/**
 * Cancel Pending Downgrade Modal
 * 
 * Modal dialog that confirms the user wants to stay on the Smart plan
 * and cancel the pending downgrade
 * 
 * @package @ideoz/ui
 */

import React from 'react';
import { X, Check } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  DialogDescription,
  DialogClose,
} from '@ideoz/ui';
import Icon from './Icon';

export interface CancelPendingDowngradeModalProps {
  isOpen: boolean;
  isLoading?: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

export default function CancelPendingDowngradeModal({
  isOpen,
  isLoading = false,
  onClose,
  onConfirm,
}: CancelPendingDowngradeModalProps): JSX.Element {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader className="flex items-center justify-between">
          <DialogTitle className="font-semibold text-[#1D2939]">
            Cancel Downgrade
          </DialogTitle>
          <DialogClose
            onClick={onClose}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-[#717680] transition-colors hover:bg-gray-100"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </DialogClose>
        </DialogHeader>

        <DialogDescription className="text-xs text-[#717680]">
          Are you sure you want to stay on the Smart plan? This will cancel your pending downgrade.
        </DialogDescription>

        {/* Info Banner */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
          <Icon icon={Check} size={20} stroke="#2563EB" />
          <div className="flex-1">
            <p className="text-sm text-blue-800">
              By confirming, you'll continue to have access to all Smart plan features and your subscription will remain active.
            </p>
          </div>
        </div>

        <DialogFooter className="flex">
          <Button
            variant="secondary-gray"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            disabled={isLoading}
            variant="primary"
          >
            {isLoading ? 'Processing...' : 'Confirm'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
